import { useEffect, useState } from "react";
import { Check, X, Trash2 } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListBusinesses, adminApproveBusiness, adminRejectBusiness, adminDeleteBusiness } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function AdminBusinesses() {
  const { toast } = useToast();
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    adminListBusinesses(filter)
      .then((d) => setItems(d.businesses || []))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function approve(id) {
    await adminApproveBusiness(id);
    toast({ title: "Goedgekeurd" });
    load();
  }
  async function reject(id) {
    await adminRejectBusiness(id);
    toast({ title: "Afgewezen" });
    load();
  }
  async function remove(id) {
    if (!window.confirm("Verwijder dit bedrijf?")) return;
    await adminDeleteBusiness(id);
    toast({ title: "Verwijderd" });
    load();
  }

  const STATUSES = [
    { value: "", label: "Alle" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Goedgekeurd" },
    { value: "rejected", label: "Afgewezen" },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-businesses-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Bedrijven</h1>
            <p className="text-ink-soft mt-1 text-sm">{items.length} resultaten</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s.value}
                onClick={() => setFilter(s.value)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  filter === s.value ? "bg-ink text-white" : "bg-white border border-border text-ink-soft hover:border-ink-soft"
                }`}
                data-testid={`biz-filter-${s.value || "all"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4">
          {loading ? (
            <p className="text-ink-soft">Laden…</p>
          ) : items.length === 0 ? (
            <p className="text-ink-soft">Geen bedrijven met deze filter.</p>
          ) : (
            items.map((b) => (
              <div key={b.id} className="rounded-2xl bg-white border border-border p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg font-semibold text-ink">{b.business_name}</h3>
                      <span
                        className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          background: b.status === "approved" ? "#10B98114" : b.status === "rejected" ? "#EF444414" : "#F59E0B14",
                          color: b.status === "approved" ? "#065F46" : b.status === "rejected" ? "#991B1B" : "#92400E",
                        }}
                      >
                        {b.status}
                      </span>
                    </div>
                    <p className="text-sm text-ink-soft mt-1">
                      {b.contact_name} · {b.email} · {b.phone || "—"}
                    </p>
                    <p className="text-xs text-ink-subtle mt-1">
                      Branches: {b.branches?.join(", ")} · Regio's: {b.regions?.join(", ")}
                    </p>
                    <p className="text-sm text-ink mt-3 max-w-2xl">{b.description}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {b.status !== "approved" && (
                      <button
                        onClick={() => approve(b.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                        data-testid={`approve-${b.id.slice(0, 6)}`}
                      >
                        <Check className="h-3.5 w-3.5" /> Goedkeuren
                      </button>
                    )}
                    {b.status !== "rejected" && (
                      <button
                        onClick={() => reject(b.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                        data-testid={`reject-${b.id.slice(0, 6)}`}
                      >
                        <X className="h-3.5 w-3.5" /> Afwijzen
                      </button>
                    )}
                    <button
                      onClick={() => remove(b.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
                      data-testid={`delete-biz-${b.id.slice(0, 6)}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Wis
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
